import { useCallback, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMobile, useMobileOrientation } from "react-device-detect";
import { ToastProvider } from "react-toast-notifications";

import ToastManager from "../ToastManager";
import MainToaster from "../Toaster";
import Router from "../Router/Router";
import Dialogs from "../Dialogs";

import {
  casinoTournamentsRequestWihoutDelay,
  selectCasinoTournament,
} from "../../store/actionCreators/casinoTournament";
import {
  openDialog,
} from "../../store/actionCreators/dialogManager";
import {
  setQueryParams,
} from "../../store/actionCreators/queryParams";
import {
  getCasinoTournament,
  getDesktopGosPopupsFromCMS,
  getIsAutoConvertToPlayerCurrencyEnabled,
  getIsOngoingTournamentsModalOpen,
  getJackpots,
  getLanguage,
  getMobileGosPopupsFromCMS,
  getRebuyTournaments,
  getRegisteredUpcomingTournaments,
  getResumeTournaments,
  getSkinId, getSkinStyle,
  getToggleJackpotGameFromHost,
  getToggleJackpotGameIdFromHost,
  getToken,
  getTournamentGameUrl,
  getUserId,
  getWallets,
  isSelectedJackpotTab,
} from "../../store/selectors";
import {
  requestTournamentGameUrl,
} from "../../store/actionCreators/tournamentGameUrl";
import { openToaster } from "../../store/actionCreators/toaster";
import {
  getAccountBalanceRequest,
} from "../../store/actionCreators/getAccountBalance";

import IsMobileContext from "../../contexts/IsMobileContext";
import SocketContext from "../../contexts/SocketContext";

import { useLanguageChange } from "../../hooks/useLanguageChange";
import { useQuery } from "../../hooks/useQuery";
import useTournamentSocket from "../../hooks/useTournamentSocket";

import { classNames, generateId, loadCSS } from "../../helpers/pureFunctions";

import config from "../../configs/config";

import {
  enalbleAutoConvertToPlayerCurrency,
  selectCurrency,
} from "../../store/actionCreators/currency";
import { getLocalExchangeRatesRequest } from "../../store/actionCreators/localExchangeRates";
import {
  requestPublicMessages,
} from "../../store/actionCreators/messages";
import { getPlayerStateInTournamentsRequest } from "../../store/actionCreators/playerStateInTournaments";
import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { setTab } from "../../store/actionCreators/tab";
import { getCMSResponseRequest } from "../../store/actionCreators/CMSResponse";
import { setOngoingTournamentsModal } from "../../store/actionCreators/onGoingTournamentsModal";
import { getGosPopupsFromCMSRequest } from "../../store/actionCreators/gosPopupsFromCMS";
import { changeTheme } from "../../helpers/theme";
import { THEME, THEME_OPTIONS } from "../../constants/theme";
import Footer from "../Footer";
import "./App.scss";

const App = (props) => {
  useGoogleAnalytics();
  const dispatch = useDispatch();
  const queries = useQuery();
  const skinId = useSelector(getSkinId);
  const userId = useSelector(getUserId);
  const token = useSelector(getToken);
  const language = useSelector(getLanguage);
  const skinStyle = useSelector(getSkinStyle);
  const jackpots = useSelector(getJackpots);
  const tournament = useSelector(getCasinoTournament);
  const tournamentGameUrl = useSelector(getTournamentGameUrl);
  const toggleJackpotGameFromHost = useSelector(getToggleJackpotGameFromHost);
  const toggleJackpotGameIdFromHost = useSelector(
    getToggleJackpotGameIdFromHost
  );
  const IsAutoConvertToPlayerCurrencyEnabled = useSelector(
    getIsAutoConvertToPlayerCurrencyEnabled
  );
  const wallets = useSelector(getWallets);
  const isJackpot = useSelector(isSelectedJackpotTab);

  const resumeTournaments = useSelector(getResumeTournaments);
  const rebuyTournaments = useSelector(getRebuyTournaments);
  const registeredUpcomingTournaments = useSelector(
    getRegisteredUpcomingTournaments
  );
  const isOngoingTournamentsModalOpen = useSelector(
    getIsOngoingTournamentsModalOpen
  );
  const DesktopGosPopupsFromCMS = useSelector(getDesktopGosPopupsFromCMS);
  const MobileGosPopupsFromCMS = useSelector(getMobileGosPopupsFromCMS);
  const gosPopupsFromCMS = isMobile
    ? MobileGosPopupsFromCMS
    : DesktopGosPopupsFromCMS;

  const { isLandscape } = useMobileOrientation();

  useEffect(() => {
    if (
      token &&
      localStorage.getItem("isAlreadyShowedOngoingTournaments") !== "true" &&
      !isOngoingTournamentsModalOpen &&
      (resumeTournaments?.length ||
        rebuyTournaments?.length ||
        registeredUpcomingTournaments?.length)
    ) {
      dispatch(openDialog({ dialogType: "onGoingEvents" }));
      dispatch(setOngoingTournamentsModal(true));
      localStorage.setItem("isAlreadyShowedOngoingTournaments", "true");
    }
  }, [
    token,
    resumeTournaments,
    rebuyTournaments,
    registeredUpcomingTournaments,
    isOngoingTournamentsModalOpen,
  ]);

  useLanguageChange(language || "en");

  const onShowBadBet = useCallback(() => {
    dispatch(openDialog({ dialogType: "badBet" }));
  }, [dispatch]);

  const onServerMessage = useCallback(
    (data) => {
      if (data.message === "tournament_finished") {
        dispatch(
          openDialog({
            dialogType: isJackpot
              ? "jackpotTournamentFinished"
              : "tournamentFinished",
            info: data,
          })
        );
        if (data?.tournamentId) {
          dispatch(
            getPlayerStateInTournamentsRequest([data?.tournamentId], true)
          );
        }
        return;
      } else if (data.message === "last_bet" && config.showLastBetToaster) {
        dispatch(
          openToaster({
            view: "tournamentLastBet",
            id: generateId(),
          })
        );
      } else if (data.message === "reloadGame" && data?.tournamentId) {
        dispatch(requestTournamentGameUrl(null, true, data.tournamentId, true));
      }
    },
    [isJackpot]
  );

  useEffect(() => {
    dispatch(
      casinoTournamentsRequestWihoutDelay({ checkResumePopup: !!queries?.token })
    );
  }, []);

  useEffect(() => {
    if (token) {
      dispatch(requestPublicMessages());
    } else {
      localStorage.removeItem("isAlreadyShowedOngoingTournaments");
    }
  }, [token]);

  useEffect(() => {
    if (
      !tournamentGameUrl &&
      toggleJackpotGameFromHost &&
      toggleJackpotGameIdFromHost &&
      jackpots?.length
    ) {
      const tournament = jackpots?.find(
        (tour) =>
          String(tour?.tournamentId) === String(toggleJackpotGameIdFromHost)
      );
      if (tournament) {
        dispatch(requestTournamentGameUrl(tournament));
        dispatch(selectCasinoTournament(tournament));
      }
    }
  }, [
    toggleJackpotGameFromHost,
    toggleJackpotGameIdFromHost,
    jackpots,
    tournamentGameUrl,
  ]);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const isVelox = urlParams.get('tab') === 'velox';
    dispatch(setTab(isVelox ? 'jackpot' : 'regular', { init: true }));
    dispatch(getCMSResponseRequest());
    dispatch(getGosPopupsFromCMSRequest());
    changeTheme(THEME[skinStyle || config?.skinStyle || THEME_OPTIONS[0].value]);
    return () => {
      localStorage.removeItem("isAlreadyShowedOngoingTournaments");
    };
  }, [skinStyle]);

  useEffect(() => {
    if (gosPopupsFromCMS?.length) {
      gosPopupsFromCMS.forEach((item) => {
        if (
          item?.imageURL &&
          item?.id &&
          localStorage.getItem(`dontShowAgain--gosPopupFromCMS--${item.id}`) !==
            "true"
        ) {
          dispatch(
            openDialog({
              dialogType: `gosPopupFromCMS--${item.id}`,
              imageURL: item.imageURL,
            })
          );
        }
      });
    }
  }, [gosPopupsFromCMS]);

  useEffect(() => {
    if (wallets?.length) {
      dispatch(getLocalExchangeRatesRequest());
    }
  }, [dispatch, wallets]);

  const emitToSocket = useTournamentSocket(
    tournament ? tournament?.tournamentId : null,
    onShowBadBet,
    onServerMessage
  );

  useLayoutEffect(() => {
    if (!props || props instanceof Object && !Object.keys(props).length) {
      dispatch(setQueryParams(queries));
    }
  }, [dispatch, queries, props]);
  
  useLayoutEffect(() => {
    if (props && props instanceof Object && Object.keys(props).length) {
      loadCSS(`${process.env.REACT_APP_ASSET_PATH}static/css/main.css?${Date.now().toString(16)}`);
      dispatch(setQueryParams(props));
    }
  }, [props]);

  useEffect(() => {
    if (userId && token) {
      dispatch(getAccountBalanceRequest(true));
    }
  }, [dispatch, userId, token]);

  useEffect(() => {
    if (tournamentGameUrl) {
      if (tournament) {
        emitToSocket("joinToGame", { tourId: tournament.tournamentId });
      }
    }
  }, [tournamentGameUrl, tournament]);

  const dontConvert = () => {
    dispatch(selectCurrency({ label: "!convert" }));
    dispatch(enalbleAutoConvertToPlayerCurrency(false));
  };

  useEffect(() => {
    if (wallets?.length) {
      const selectedCurrencyStorage = JSON.parse(
        localStorage.getItem("selectedCurrency")
      );
      if (selectedCurrencyStorage) {
        dispatch(selectCurrency(selectedCurrencyStorage));
        dispatch(
          enalbleAutoConvertToPlayerCurrency(
            selectedCurrencyStorage.label !== "!convert"
          )
        );
      } else {
        if (IsAutoConvertToPlayerCurrencyEnabled) {
          dispatch(selectCurrency({ label: wallets[0].currency }));
          localStorage.setItem(
            "selectedCurrency",
            JSON.stringify({ label: wallets[0].currency })
          );
        } else {
          dontConvert();
          localStorage.setItem(
            "selectedCurrency",
            JSON.stringify({ label: "!convert" })
          );
        }
      }
    }
  }, [IsAutoConvertToPlayerCurrencyEnabled, wallets]);

  const appClassNames = classNames("app", {
    landscape: isLandscape,
    mobile: isMobile,
    desktop: !isMobile,
  });

  return (
    <>
      <ToastProvider>
        <ToastManager />
        <div className={appClassNames}>
          <div className="container">
            <div className="app__inner">
              {skinId && (
                <SocketContext.Provider value={{ emitToSocket }}>
                  <IsMobileContext.Provider value={{ isMobile }}>
                    <Dialogs />
                    <MainToaster />
                    <Router />
                  </IsMobileContext.Provider>
                </SocketContext.Provider>
              )}
            </div>
          </div>
          <div className="app__footer-wrapper">
            <Footer />
          </div>
        </div>
      </ToastProvider>
    </>
  );
};

export default App;
